<template>
  <m-field
    class="field-wrapper"
    :data-testid="testId"
    :status="hasError ? 'danger' : 'default'"
    :class="`drop-down-${$.uid}`">
    <m-field-label slot="label" v-if="label">
      {{ label }}

      <help-tooltip v-if="hint" :text="hint" />
    </m-field-label>
    <vuetify-auto-complete
      density="compact"
      :data-testid="`${testId}-trigger`"
      :multiple="multiple"
      variant="outlined"
      :items="options"
      hide-details="hide"
      :label="null"
      item-title="label"
      item-value="value"
      :model-value="displayModelValue"
      :clearable="multiple"
      :style="computedWidth"
      :menu-props="{
        location: menuLocation,
        offset: 4,
        rounded: true,
        ripple: false,
        attach: `.drop-down-${$.uid}`
      }"
      @update:model-value="handleSelection">
      <template v-if="multiple" #selection="{ index }">
        <span v-if="index === 0" class="selection-summary" :title="selectionSummary">
          {{ selectionSummary }}
        </span>
      </template>
      <template #item="{ item, props: itemProps }">
        <v-list-item
          v-bind="getListItemBindProps(itemProps)"
          :class="{ selected: isSelected(item.raw) }"
          :data-testid="`${testId}-menu-item`"
          class="px-5"
          @click="onListItemClick(item.raw, itemProps, $event)">
          {{ item.label }}
          <basic-icon v-if="isSelected(item.raw)">check</basic-icon>
        </v-list-item>
      </template>
    </vuetify-auto-complete>
    <m-field-hint :data-testid="`${testId}-errors`" v-if="errorBucket.length">
      {{ errorBucket.join(', ') }}
    </m-field-hint>
  </m-field>
</template>

<script>
import { BasicIcon, HelpTooltip } from '@/components';
import { uniqDropDownMultiSelectSelection, uniqDropDownMultiSelectValues } from '@nova/core';
import { isEqual, some } from 'lodash';
import fieldMixin from '@/components/mixins/fieldMixin';
import validatable from '@/components/mixins/validatable';
import testable from '@/components/mixins/testable';
import { validateV2DropDownOptionsProp } from '@satellite/plugins/util';
import { VAutocomplete as VuetifyAutoComplete } from 'vuetify/lib/components/VAutocomplete/VAutocomplete.mjs';

/**
 * DropDownField wraps Mirdanda WC library's <m-field> and <m-dropdown> components
 * Keeping separate from Multi Drop Down because it is Vuetify
 * @displayName Drop Down Field
 */
export default {
  name: 'DropDownField',
  mixins: [fieldMixin, validatable, testable],
  components: { VuetifyAutoComplete, BasicIcon, HelpTooltip },
  props: {
    /**
     * null type to allow "any"
     * @model
     */
    modelValue: {
      type: null,
      required: false
    },
    /**
     * Field options
     */
    options: {
      type: Array,
      required: false,
      default() {
        return [];
      },
      validator(value) {
        return validateV2DropDownOptionsProp(value, 'DropDownField');
      }
    },
    /**
     * Allow multiple choices
     */
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Preferred side for the options menu; Vuetify flips it when there is no room
     */
    menuLocation: {
      type: String,
      required: false,
      default: 'top'
    }
  },
  computed: {
    displayModelValue() {
      if (!this.multiple) {
        return this.modelValue;
      }
      return uniqDropDownMultiSelectSelection(this.modelValue);
    },
    selectionSummary() {
      return this.options
        .filter(option =>
          some(this.displayModelValue, value => isEqual(this.getOptionValue(option), value))
        )
        .map(option => option.label)
        .join(', ');
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        if (!this.multiple || !Array.isArray(value)) {
          return;
        }
        const dedupedValue = uniqDropDownMultiSelectValues(value);
        if (!isEqual(dedupedValue, value)) {
          this.internalValue = dedupedValue;
        }
      }
    }
  },
  methods: {
    getListItemBindProps(itemProps) {
      if (!this.multiple) {
        return itemProps;
      }
      const { onClick, onclick, ...listItemPropsWithoutClick } = itemProps;
      return listItemPropsWithoutClick;
    },
    getOptionValue(option) {
      return option?.value ?? option;
    },
    isSelected(option) {
      const optionValue = this.getOptionValue(option);
      if (this.multiple) {
        return some(this.displayModelValue, val => isEqual(optionValue, val));
      }
      return isEqual(optionValue, this.modelValue);
    },
    onListItemClick(option, itemProps, event) {
      if (this.multiple) {
        event.preventDefault();
        event.stopPropagation();
        this.onMultiSelectItemClick(option);
        return;
      }
      itemProps.onClick?.(event);
    },
    onMultiSelectItemClick(option) {
      const optionValue = this.getOptionValue(option);
      let nextSelection = [...this.displayModelValue];

      if (this.isSelected(option)) {
        nextSelection = nextSelection.filter(selectedValue => !isEqual(selectedValue, optionValue));
      } else {
        nextSelection.push(optionValue);
      }

      this.handleSelection(nextSelection);
    },
    handleSelection(event) {
      const nextValue = this.multiple ? uniqDropDownMultiSelectValues(event) : event;
      if (this.multiple && isEqual(nextValue, this.displayModelValue)) {
        return;
      }
      // Duplicate option values can cause Vuetify to emit repeated selections; keep values unique.
      this.internalValue = nextValue;
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.v-list-item__content) {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.v-input {
  margin-top: 0 !important;
}
.field-wrapper {
  flex: 1;
  position: relative;
}
.dropdown-menu {
  max-height: 10rem;
  width: 100%;
}
.select-option {
  justify-content: space-between;
}
.selected {
  background-color: $m-color-background-highlight;
  font-weight: $m-font-weight-bold;
}

m-dropdown-item {
  width: 100%;
  justify-content: space-between;
}

:deep(.v-input) {
  padding: 0;
  margin: 0;
}
:deep(.v-input__slot) {
  &:before,
  &:after {
    display: none;
  }
}

:deep(.v-field__input) {
  opacity: 1;
  flex-wrap: nowrap;
  overflow: hidden;

  input {
    flex: 1 1 0;
    min-width: 0;
  }
}

.selection-summary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.v-select__slot) {
  min-height: 40px !important;
  max-height: 40px !important;
  --m-dropdown-trigger-background-color: rgba(var(--m-color-background-primary, 252, 252, 255), 1);
  --m-dropdown-trigger-border-color: rgba(var(--m-color-border, 193, 206, 217), 1);
  --m-dropdown-trigger-border-radius: var(--m-border-radius-s, 4px);
  --m-dropdown-trigger-color: rgba(var(--m-color-text-tertiary, 92, 106, 118), 1);
  --m-dropdown-trigger-padding-x: var(--m-spacing-2, 8px);
  --m-dropdown-trigger-padding-y: var(--m-spacing-2, 8px);
  --m-dropdown-trigger-height: var(--m-global-height-default, 2.5rem);
  transition:
    color 0.35s ease-in-out 0s,
    background-color 0.35s ease-in-out 0s,
    border-color 0.35s ease-in-out 0s;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  font-family: var(--m-font-family-default, 'Manrope', sans-serif);
  line-height: var(--m-line-height-5, 150%);
  font-size: var(--m-font-size-3, 0.875rem);
  font-weight: var(--m-font-weight-regular, 500);
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  block-size: var(--m-dropdown-trigger-height);
  inline-size: 100%;
  box-sizing: border-box;
  text-decoration: none;
  gap: var(--m-spacing-1, 4px);
  padding-inline-end: var(--m-dropdown-trigger-padding-x);
  padding-inline-start: var(--m-dropdown-trigger-padding-x);
  padding-block-start: var(--m-dropdown-trigger-padding-y);
  padding-block-end: var(--m-dropdown-trigger-padding-y);
  background-color: var(--m-dropdown-trigger-background-color);
  color: var(--m-dropdown-trigger-color);
  border: 1px solid var(--m-dropdown-trigger-border-color);
  border-radius: var(--m-dropdown-trigger-border-radius);
  outline: none;
}
:deep(.v-input__append-inner) {
  margin-top: $m-spacing-0-5 !important;
  margin-right: -2px;
}

:deep(.v-list-item) {
  padding: 0 $m-spacing-2;
  &.v-list-item--highlighted {
    background-color: $m-color-background-primary;
  }
}

:deep(.v-list-item__content) {
  padding: 0;
}

:deep(.v-list-item--link:before) {
  display: none;
}

:deep(.v-list-item--active .v-list-item__title) {
  background: $m-color-background-tertiary;
}

:deep(.v-list-item__title) {
  padding: 0 $m-spacing-1 !important;
  --m-dropdown-item-background-color: rgba(var(--m-color-background-primary, 252, 252, 255), 1);
  --m-dropdown-item-color: rgba(var(--m-color-text-secondary, 61, 74, 85), 1);
  transition:
    color 0.35s ease-in-out 0s,
    background-color 0.35s ease-in-out 0s;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  font-family: var(--m-font-family-default, 'Manrope', sans-serif);
  line-height: var(--m-line-height-5, 150%);
  font-size: var(--m-font-size-3, 0.875rem);
  font-weight: var(--m-font-weight-regular, 500);
  block-size: 36px;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
  gap: var(--m-spacing-2, 8px);
  padding-inline-end: var(--m-spacing-1, 4px);
  padding-inline-start: var(--m-spacing-1, 4px);
  padding-block-start: var(--m-spacing-2, 8px);
  padding-block-end: var(--m-spacing-2, 8px);
  background-color: var(--m-dropdown-item-background-color);
  color: var(--m-dropdown-item-color);
  border: none;
  border-radius: var(--m-border-radius-s, 4px);
}

:deep(.v-ripple__container) {
  display: none !important;
}

:deep(.v-list-item__action) {
  display: none;
}

:deep(.v-menu__content) {
  transform: translateY(25px);
}
</style>
